import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function useModalFocusTrap({ active, containerRef, initialFocusRef, restoreFocusRef }) {
  useEffect(() => {
    if (!active) return undefined;

    const container = containerRef.current;
    const previouslyFocused = document.activeElement;
    // Captured at setup, not read in cleanup: react-hooks/exhaustive-deps warns
    // that a ref read during cleanup may already have changed, and CRA promotes
    // that warning to a build error. The owning control does not change identity
    // while the modal is open, so capturing here is equivalent and lint-clean.
    const restoreTarget = restoreFocusRef?.current || null;
    const focusable = () => Array.from(container?.querySelectorAll(FOCUSABLE_SELECTOR) || []);
    (initialFocusRef?.current || focusable()[0])?.focus();

    const handleKeyDown = (event) => {
      if (event.key !== 'Tab') return;

      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (!container?.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Prefer an EXPLICIT restore target over whatever happened to hold focus
      // when the trap opened. A click does not focus a button in jsdom (and not
      // in Safari either), so `previouslyFocused` is often document.body -- the
      // caller usually knows which control owns the modal and should get focus
      // back. Fall back to the previous element when no ref is supplied.
      const restoreTo = restoreTarget || previouslyFocused;
      if (restoreTo instanceof HTMLElement && document.contains(restoreTo)) {
        restoreTo.focus();
      }
    };
  }, [active, containerRef, initialFocusRef, restoreFocusRef]);
}

export default useModalFocusTrap;
